import { triplabotVersion } from 'javascript/Constants'
import { dateTimeFormat } from 'Utilities/TimerHelper'
import _isInteger from 'lodash/isInteger'
import Vue from 'vue'
import { collectAdditionalDetails } from 'javascript/helpers'
import triplaLocalStorage from 'Utilities/triplaLocalStorage'
import { triplaServer } from 'src/apis/server'
import { serialize } from 'object-to-formdata'
import { useAppStore } from 'Store/app'
import { useChatMenuStore } from 'Store/chat-menu'
import { useChatStore } from 'Store/chat'

let previousAdditionalDetails = {}

export function sendFile(file, fileIndex = 0, fileAttribute = 'file') {
  const chatStore = useChatStore()
  const formData = new FormData()
  formData.append(fileAttribute, file, file.name)
  formData.append('message', file.name)
  formData.append('file_index', fileIndex)
  formData.append('is_manual_from_user', true)
  return triplaServer.post(`api/v1/chats/${chatStore.chatId}/chat_messages`, formData)
}

export function sendMessage(message, options = {}) {
  const appStore = useAppStore()
  const chatMenuStore = useChatMenuStore()
  const chatStore = useChatStore()

  const chatID = chatStore.chatId
  // check if chat ID is loaded successfully before sending messages
  // AND check if the input is not a whitespace
  const isMessageEmpty = !/\S/.test(message)
  const hasContent = !isMessageEmpty || options.customBotFormFields
  if (!chatID || !hasContent) {
    return Promise.resolve()
  }

  const formData = {
    message: message.trim(),
    triplabot_version: triplabotVersion,
    message_id: options.messageID,
    message_action: options.messageAction,
    message_action_id: options.messageActionId,
    reset: options.resetValue,
    use_ai: options.useAi,
    auto_message: options.autoMessageFaqCode,
    auto_message_type: options.autoMessageType,
    change_language: options.changeLanguage,
    card_token: options.card_token,
    payment_gateway: options.payment_gateway,
    paypal_order_id: options.paypal_order_id,
    amount: options.amount,
    latitude: options.latitude,
    longitude: options.longitude,
    icon_message_id: options.iconMessageID,
    custom_bot_form_data: options.customBotFormFields,
    is_manual_from_user: options.isManualFromUser
  }

  try {
    const additionalDetails = collectAdditionalDetails()
    if (JSON.stringify(additionalDetails) !== JSON.stringify(previousAdditionalDetails)) {
      formData.analytics = additionalDetails
      previousAdditionalDetails = additionalDetails
    }
  } catch (error) {
    Vue.rollbar.error('failed to collect additional details', error)
  }

  appStore.setInputMessage('')
  chatMenuStore.setOpen(false)

  return triplaServer.post(
    `api/v1/chats/${chatID}/chat_messages`,
    serialize(formData, { nullsAsUndefineds: true, allowEmptyArrays: false })
  )
}

export function GetChat(currentChat, params) {
  if (currentChat !== null) {
    const chatResult = { data: { chat: currentChat } }
    return Promise.resolve(chatResult)
  }

  return triplaServer
    .post(
      'api/v1/chats/triplabot',
      serialize({
        ...params,
        token: triplaLocalStorage.getItem('triplabot_token')
      })
    )
    .then((res) => res.data)
}

export function GetBranchChatsCount(chatID) {
  return triplaServer
    .get(`api/triplabot/v1/chats/${chatID}/active_branch_chats_count`)
    .then((res) => res.data)
}

export function TriggerVisitedBranchList(chatID) {
  return triplaServer
    .put(`api/triplabot/v1/chats/${chatID}/active_branch_chats_selector`)
    .then((res) => res.data)
}

export function GetChatMessages(chatID, params = {}) {
  return triplaServer
    .get(`api/v1/chats/${chatID}/chat_messages`, {
      params: {
        custom_bot_test: params.custom_bot_test || undefined
      }
    })
    .then((res) => res.data)
}

export function SendChatEval(messageID, rating) {
  return triplaServer
    .put(
      `api/triplabot/v1/messages/${messageID}/rate`,
      serialize({
        rating
      })
    )
    .then((res) => res.data)
}

export function SendEvaluations(evaluation, evaluableDetailed) {
  const payload = {
    evaluation: evaluation,
    evaluables: evaluableDetailed
  }
  return triplaServer
    .post(`api/triplabot/v1/evaluations/bulk_upsert`, serialize(payload))
    .then((res) => res.data)
}

export function SendUserPreference(preference) {
  const payload = {
    user_evaluation_subscribed: preference
  }
  return triplaServer
    .post(`api/triplabot/v1/user_preference`, serialize(payload))
    .then((res) => res.data)
}

export function SendUserEval(messageID, rating) {
  return triplaServer
    .put(
      `api/v1/chat_messages/${messageID}/evaluate`,
      serialize({
        customer_evaluation: rating
      })
    )
    .then((res) => res.data)
}

export function callOperator(messageID, messageAction, message, userChatMessageId) {
  return triplaServer
    .put(
      `api/triplabot/v1/messages/${messageID}/call_op`,
      serialize({
        message,
        user_chat_message_id: userChatMessageId,
        op_mode: messageAction === 'yes'
      })
    )
    .then((res) => res.data)
}

export function sendForm(chatID, payload) {
  return triplaServer
    .post(`api/triplabot/v1/chats/${chatID}/auto_message_form_submissions`, serialize(payload))
    .then((res) => res.data)
}

// API call that will trigger all non-phone auto messages on chat open
export function chatOpenTrigger(chatID, triggerValue) {
  return triplaServer
    .put(
      `api/triplabot/v1/chats/${chatID}/chat_open_trigger`,
      serialize({
        trigger: triggerValue
      })
    )
    .then((res) => res.data)
}

// API call triggered by best room rate widget to make reservation using specific plan
export function bestRoomAction(endpoint, messageID, payload) {
  return triplaServer
    .put(
      `api/triplabot/v1/messages/${messageID}/${endpoint}`,
      serialize({
        payload: JSON.stringify(payload)
      })
    )
    .then((res) => res.data)
}

export function sendIconImageMessage(chatID, params = {}) {
  return triplaServer
    .post(
      `api/triplabot/v1/chats/${chatID}/icon_message_image_reply`,
      serialize(
        {
          icon_message_id: params.iconMessageID,
          message_action: params.messageAction
        },
        { nullsAsUndefineds: true }
      )
    )
    .then((res) => res.data)
}

export function changeChat(triplabotCode, options = {}) {
  // options: { action, paramName, paramValue }
  if (!triplabotCode) {
    return Promise.resolve()
  }
  const form = {
    code: triplabotCode,
    action: options.action,
    original_chat_id: options.original_chat_id
  }

  if (options.paramName && options.paramValue) {
    form[options.paramName] = options.paramValue
  }

  return triplaServer
    .post('api/triplabot/v1/chats/change_chat', serialize(form, { nullsAsUndefineds: true }))
    .then((res) => res.data)
}

export function sendClosingResponse(messageID, messageAction) {
  const answer = /^closing_(.*)/.exec(messageAction)[1]
  return triplaServer
    .put(
      `api/triplabot/v1/messages/${messageID}/closing`,
      serialize({
        answer
      })
    )
    .then((res) => res.data)
}

export function getRentalCarShops(chatId, prefectureId) {
  return triplaServer
    .get('api/triplabot/v1/rental_car/shops', {
      params: {
        chat_id: chatId,
        prefecture_id: prefectureId
      }
    })
    .then((res) => res.data)
}

export function getRentalCarTimeList(shopId, pickupTime) {
  return triplaServer
    .get('api/triplabot/v1/rental_car/holidays', {
      params: {
        shop_id: shopId,
        pickup_time: pickupTime ? dateTimeFormat(pickupTime) : undefined
      }
    })
    .then((res) => res.data)
}

export function goToState(chatID, state, restartStepId, currentStepId) {
  const form = {
    state,
    restart_step_id: restartStepId,
    current_step_id: _isInteger(currentStepId) ? currentStepId : undefined
  }

  return triplaServer
    .put(`api/triplabot/v1/chats/${chatID}/state`, serialize(form, { nullsAsUndefineds: true }))
    .then((res) => res.data)
}

export function getCheapestPrice(
  chatID,
  { roomTypeCode, checkinDate, checkoutDate, hotelID, onlyCheckDates }
) {
  return triplaServer
    .get(`api/v1/hotels/${hotelID}/hotel_rooms/cheapest_price`, {
      params: {
        room_type_code: roomTypeCode,
        price_date: checkinDate,
        chat_id: chatID,
        checkout_date: checkoutDate,
        only_check_dates: onlyCheckDates
      }
    })
    .then((res) => res.data)
}

export function nlpFeedback(request, isCorrect = false) {
  const chatStore = useChatStore()

  return triplaServer
    .put(
      `api/triplabot/v1/chats/${chatStore.chatId}/nlp_feedback`,
      serialize({
        request,
        is_correct: isCorrect
      })
    )
    .then((res) => res.data)
}

export function getRestaurantReservableTimes(shopId, date, guestNumber) {
  return triplaServer
    .get('api/triplabot/v1/restaurants/reservable_times', {
      params: {
        shop_id: shopId,
        date: date,
        guest_number: guestNumber
      }
    })
    .then((res) => res.data)
}

export function initAppChat() {
  return triplaServer.post('app/chats/init').then((res) => res.data)
}

export function getPrefecturesAndAreas(chatID) {
  return triplaServer
    .get('restaurants/prefectures_areas', {
      params: {
        chat_id: chatID
      }
    })
    .then((res) => res.data)
}

function deleteMessage(msgId) {
  return triplaServer.delete(`api/v1/chat_messages/${msgId}`)
}

export default {
  sendFile,
  sendMessage,
  GetChat,
  GetBranchChatsCount,
  TriggerVisitedBranchList,
  GetChatMessages,
  SendChatEval,
  SendUserEval,
  SendEvaluations,
  SendUserPreference,
  callOperator,
  sendForm,
  chatOpenTrigger,
  bestRoomAction,
  sendIconImageMessage,
  changeChat,
  sendClosingResponse,
  getRentalCarShops,
  getRentalCarTimeList,
  goToState,
  getCheapestPrice,
  nlpFeedback,
  getRestaurantReservableTimes,
  initAppChat,
  deleteMessage,
  getPrefecturesAndAreas,
  choosePaymentMethod(chatId, data) {
    return triplaServer
      .post(`api/v1/chats/${chatId}/chat_messages`, serialize(data))
      .then((res) => res.data)
  },

  sendEvaluation(chatId, evaluation) {
    return triplaServer.post(`api/triplabot/v1/chats/${chatId}/evaluations`, { evaluation })
  }
}
